import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';
import { routePath } from '../i18n/routes';
import { LEGAL_NOTICE_TITLES } from '../i18n/legalNotice';

const PUBLISHER_HEADING = {
  fr: 'Éditeur du site (informations conformes au § 5 DDG pour l\u2019Allemagne)',
  de: 'Angaben gemäß § 5 DDG',
  en: 'Site publisher (information pursuant to § 5 DDG for Germany)',
  es: 'Editor del sitio (información conforme al § 5 DDG para Alemania)',
  it: 'Editore del sito (informazioni ai sensi del § 5 DDG per la Germania)',
  zh: '网站运营方（依据德国《数字服务法》§ 5 DDG 的信息）'
};

const DESCRIPTIONS = {
  fr: 'Mentions légales d\u2019OptimHub SAS : éditeur du site, contact, immatriculation RCS et TVA, directeur de la publication, hébergeur et règlement des litiges de consommation.',
  de: 'Impressum von OptimHub SAS: Angaben gemäß § 5 DDG, Kontakt, Handelsregister und USt-IdNr., verantwortlich für den Inhalt, Hosting und Verbraucherstreitbeilegung.',
  en: 'Legal notice of OptimHub SAS: site publisher, contact, trade register and VAT number, publication director, hosting provider and consumer dispute resolution.',
  es: 'Aviso legal de OptimHub SAS: editor del sitio, contacto, registro mercantil y número de IVA, director de publicación, alojamiento y resolución de litigios de consumo.',
  it: 'Note legali di OptimHub SAS: editore del sito, contatti, registro delle imprese e partita IVA, direttore della pubblicazione, hosting e risoluzione delle controversie.',
  zh: 'OptimHub SAS 法律声明：网站运营方、联系方式、商业登记与增值税号、出版负责人、托管服务商及消费争议解决。'
};

const LegalNotice = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      heading: PUBLISHER_HEADING[currentLanguage] || PUBLISHER_HEADING.en,
      lines: [
        { value: 'OptimHub SAS', strong: true },
        { value: '11 rue de Lourmel, 75015 Paris, France' },
        { label: l('Représentée par'), value: `Julien Bosselut, ${l('Président')}` }
      ]
    },
    {
      heading: l('Contact'),
      lines: [{ label: l('E-mail'), value: 'contact@optimhub.com', mailto: true }]
    },
    {
      heading: l('Immatriculation'),
      lines: [
        { value: l('Registre du commerce et des sociétés (RCS) de Paris') },
        { label: l('Numéro'), value: '534 057 088' },
        { label: l('Numéro de TVA intracommunautaire'), value: 'FR63534057088' },
        { label: l('Capital social'), value: '5 001 EUR' },
        { label: l('Directeur de la publication'), value: 'Julien Bosselut' }
      ]
    },
    {
      heading: l('Hébergeur'),
      lines: [
        { value: 'Emergent', strong: true },
        { value: 'https://app.emergent.sh' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50" data-testid="legal-notice-page">
      <SEOHead
        title={`${LEGAL_NOTICE_TITLES[currentLanguage] || LEGAL_NOTICE_TITLES.en} — OptimHub`}
        description={DESCRIPTIONS[currentLanguage] || DESCRIPTIONS.en}
        routeKey="legalNotice"
        lang={currentLanguage}
      />
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <Button
          onClick={() => navigate(routePath('home', currentLanguage))}
          variant="ghost"
          size="sm"
          className="mb-6"
          data-testid="legal-notice-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {l('Retour')}
        </Button>

        <Card className="shadow-xl">
          <CardHeader className="pb-6">
            <CardTitle className="text-3xl font-bold text-gray-900" data-testid="legal-notice-title">
              {LEGAL_NOTICE_TITLES[currentLanguage] || LEGAL_NOTICE_TITLES.en}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="space-y-10">
              {sections.map((section, index) => (
                <section key={section.heading} data-testid={`legal-notice-section-${index}`}>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.heading}</h2>
                  <div className="space-y-1 text-gray-700 leading-relaxed">
                    {section.lines.map((line, i) => (
                      <p key={i}>
                        {line.label && (
                          <span className="text-gray-500">
                            {line.label}
                            {currentLanguage === 'fr' ? ' : ' : currentLanguage === 'zh' ? '：' : ': '}
                          </span>
                        )}
                        {line.mailto ? (
                          <a href={`mailto:${line.value}`} className="text-blue-600 hover:text-blue-800">
                            {line.value}
                          </a>
                        ) : (
                          <span className={line.strong ? 'font-semibold text-gray-900' : undefined}>
                            {line.value}
                          </span>
                        )}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <section data-testid="legal-notice-dispute">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {l('Règlement des litiges de consommation')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {l("Nous ne sommes ni disposés ni tenus de participer à une procédure de règlement des litiges devant un organisme de médiation de la consommation.")}
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default LegalNotice;
